import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isValidPassword, getPasswordValidationErrors } from '../../utils/validators';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import { debug } from '../../utils/debug';

const AdminSignup: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const { signup } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    // Get admin email from session storage
    const adminEmail = sessionStorage.getItem('adminEmail');
    
    if (!adminEmail) {
      // Redirect to admin check if no email is stored
      navigate('/admin');
      return;
    }
    
    setEmail(adminEmail);
  }, [navigate]);
  
  useEffect(() => {
    if (password) {
      setPasswordErrors(getPasswordValidationErrors(password));
    } else {
      setPasswordErrors([]);
    }
  }, [password]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    debug.log('Admin signup form submitted', { email });
    
    // Clear previous errors
    setError(null);
    
    // Validate password
    if (!isValidPassword(password)) {
      debug.log('Password validation failed');
      setError('Please fix the password issues below');
      return;
    }
    
    // Check if passwords match
    if (password !== confirmPassword) {
      debug.log('Passwords do not match');
      setError('Passwords do not match');
      return;
    }
    
    setLoading(true);
    debug.log('Starting signup process');
    
    try {
      debug.log('Calling signup function');
      await signup(email, password);
      
      // Clear session storage
      sessionStorage.removeItem('adminEmail');
      debug.log('Admin email removed from session storage');
      
      // Show verification message and redirect to login
      alert('Verification email sent. Please check your inbox and verify your email before logging in.');
      debug.log('Redirecting to login page');
      navigate('/admin/login');
    } catch (err) {
      debug.error('Signup error:', err);
      console.error('Signup error:', err);
      if (err instanceof Error) {
        debug.error('Error message:', err.message);
        setError(err.message);
      } else {
        setError('Failed to create an account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Create Admin Account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Set up your administrator account to manage the site
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <Input
              type="email"
              id="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              label="Email address"
              required
              disabled
              autoComplete="email"
            />

            <Input
              type="password"
              id="password"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              label="Password"
              required
              autoComplete="new-password"
            />
            
            {passwordErrors.length > 0 && (
              <div className="text-sm text-red-600">
                <ul className="list-disc list-inside">
                  {passwordErrors.map((err, index) => (
                    <li key={index}>{err}</li>
                  ))}
                </ul>
              </div>
            )}

            <Input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              label="Confirm password"
              required
              autoComplete="new-password"
              error={confirmPassword && password !== confirmPassword ? 'Passwords do not match' : ''}
            />

            {error && (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded">
                {error}
              </div>
            )}

            <div>
              <Button type="submit" fullWidth disabled={loading}>
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </div>
          </form>
          
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  Already have an account?
                </span>
              </div>
            </div>

            <div className="mt-6 text-center">
              <button
                onClick={() => navigate('/admin/login')}
                className="text-blue-600 hover:text-blue-500 font-medium"
              >
                Sign in instead
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSignup;